import type Map from 'ol/Map';
import { toLonLat } from 'ol/proj';
import { captureMap } from '@/utils/mapCapture';
import { useLayerStore } from '@/store/layer.store';
import { layerLabel } from '@/utils/layerLabel';
import apiClient from '@/services/api';
import type { MapSnapshot } from './model';

/** Longest side of the kept map picture: enough for a page, small enough to save with the report. */
const MAX_SIDE = 1400;

function load(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** The map as it is now — the catchments drawn on it and the layers shown — as a JPEG, with what was on it. */
export async function snapshotMap(map: Map | null): Promise<MapSnapshot | undefined> {
  if (!map) return undefined;
  try {
    const full = await captureMap(map);
    const img = await load(full);
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const view = map.getView();
    const c = view.getCenter();
    return {
      image: canvas.toDataURL('image/jpeg', 0.85),
      width: w,
      height: h,
      center: c ? (toLonLat(c) as [number, number]) : undefined,
      zoom: view.getZoom(),
      layers: shownLayers(),
      takenAt: new Date().toISOString(),
    };
  } catch (e) {
    console.warn('map snapshot failed', e);
    return undefined;
  }
}

/** The names of the layers shown on the map now. */
export function shownLayers(): string[] {
  const { allLayers, visibility } = useLayerStore.getState();
  return allLayers.filter((l) => visibility[l.id]).map((l) => layerLabel(l));
}

let logoCache: Promise<string | undefined> | null = null;

/** The company's logo as a data URL, for the report's outputs; nothing when the company has none. */
export function companyLogo(): Promise<string | undefined> {
  if (!logoCache) {
    logoCache = apiClient
      .get<Blob>('/company/logo', { responseType: 'blob' })
      .then(
        (r) =>
          new Promise<string | undefined>((resolve) => {
            const blob = r.data;
            if (!blob || !/^image\/(png|jpe?g)/.test(blob.type)) {
              // PDF and Excel take PNG and JPEG: anything else is drawn through a canvas first
              if (blob && blob.type.startsWith('image/')) {
                const url = URL.createObjectURL(blob);
                load(url).then((img) => {
                  const c = document.createElement('canvas');
                  c.width = img.naturalWidth;
                  c.height = img.naturalHeight;
                  c.getContext('2d')?.drawImage(img, 0, 0);
                  URL.revokeObjectURL(url);
                  resolve(c.toDataURL('image/png'));
                }).catch(() => resolve(undefined));
                return;
              }
              resolve(undefined);
              return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
            reader.onerror = () => resolve(undefined);
            reader.readAsDataURL(blob);
          }),
      )
      .catch(() => undefined);
  }
  return logoCache;
}

/**
 * A small picture of the report's map for the list of saved reports: a JPEG at most 320 px wide, a few tens of KB.
 * Nothing when the report has no map or the picture cannot be made.
 */
export async function thumbnailOf(snapshot: { image: string } | undefined): Promise<string | undefined> {
  if (!snapshot?.image) return undefined;
  try {
    const img = await load(snapshot.image);
    const scale = Math.min(1, 320 / Math.max(img.width, 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const q of [0.7, 0.5, 0.35]) {
      const url = canvas.toDataURL('image/jpeg', q);
      // base64 is 4/3 of the bytes; the server keeps at most 96 KB
      if (url.length * 0.75 < 60 * 1024) return url;
    }
    return undefined;
  } catch {
    return undefined;
  }
}
